/**
 * Ticket Service
 *
 * Handles all ticket CRUD operations including:
 * - Creating new tickets
 * - Updating existing tickets
 * - Fetching ticket data for prefill
 * - Submitting tickets for production
 * - Creating modified tickets
 */

import { fetchWithAuthRetry } from "@/utils/auth-helpers";
import { logger } from "@/utils/logger";
import { getPresetLabel } from "@/components/create/presets";
import { UPLOAD_TICKET_PROMPT, DEFAULT_UPLOAD_PROMPT } from "@/utils/constants";
import type { DressDesignFields, DressDesignUpdate } from "@/types/dressForm";
import type { DesignSpec } from "@/types/designSpec";

const CREATE_TICKET_INTENT_STORAGE_KEY = "ticket.create.intent.v1";

type CreateTicketIntent = {
  fingerprint: string;
  idempotencyKey: string;
};

let inMemoryCreateTicketIntent: CreateTicketIntent | null = null;

type TicketCas = {
  updatedAt: string;
  revisionCounter: number;
};

const inMemoryTicketCas = new Map<string, TicketCas>();

const ticketCasStorageKey = (ticketId: string): string =>
  `ticket.update.cas.v1.${ticketId}`;

const forgetTicketCas = (ticketId: string): void => {
  inMemoryTicketCas.delete(ticketId);
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(ticketCasStorageKey(ticketId));
  } catch {
    // The in-memory copy has already been cleared.
  }
};

const normalizeTicketCasTimestamp = (value: unknown): string | null => {
  if (typeof value !== "string") return null;

  // PostgREST may expose a PostgreSQL timestamptz as
  // `2026-08-07 17:37:25.721529+00`. Convert only the syntax to RFC 3339 and
  // preserve all fractional digits: Date#toISOString would truncate the
  // database's microseconds and make the exact CAS comparison fail.
  const normalized = value
    .trim()
    .replace(/^(\d{4}-\d{2}-\d{2})\s+/, "$1T")
    .replace(/\+00(?::00)?$/, "Z");

  if (
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/.test(normalized) ||
    !Number.isFinite(Date.parse(normalized))
  ) {
    return null;
  }

  return normalized;
};

const createTicketCas = (
  updatedAt: unknown,
  revisionCounter: unknown
): TicketCas | null => {
  const normalizedUpdatedAt = normalizeTicketCasTimestamp(updatedAt);
  if (
    !normalizedUpdatedAt ||
    !Number.isInteger(revisionCounter) ||
    (revisionCounter as number) < 0
  ) {
    return null;
  }

  return {
    updatedAt: normalizedUpdatedAt,
    revisionCounter: revisionCounter as number,
  };
};

const readTicketCas = (ticketId: string): TicketCas | null => {
  if (typeof window !== "undefined") {
    try {
      const value = window.sessionStorage.getItem(
        ticketCasStorageKey(ticketId)
      );
      if (value) {
        const parsed = JSON.parse(value) as Partial<TicketCas>;
        const snapshot = createTicketCas(
          parsed.updatedAt,
          parsed.revisionCounter
        );
        if (snapshot) return snapshot;
        window.sessionStorage.removeItem(ticketCasStorageKey(ticketId));
      }
    } catch {
      // Fall through to the in-memory snapshot.
    }
  }

  const inMemorySnapshot = inMemoryTicketCas.get(ticketId);
  if (!inMemorySnapshot) return null;
  const snapshot = createTicketCas(
    inMemorySnapshot.updatedAt,
    inMemorySnapshot.revisionCounter
  );
  if (!snapshot) inMemoryTicketCas.delete(ticketId);
  return snapshot;
};

const rememberTicketCas = (ticket: Ticket): void => {
  if (typeof ticket.id !== "string") return;
  const snapshot = createTicketCas(ticket.updated_at, ticket.revision_counter);
  if (!snapshot) {
    forgetTicketCas(ticket.id);
    return;
  }
  inMemoryTicketCas.set(ticket.id, snapshot);
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      ticketCasStorageKey(ticket.id),
      JSON.stringify(snapshot)
    );
  } catch {
    // The in-memory copy still protects concurrent updates in this page.
  }
};

const createTicketIntentFingerprint = (
  requestBody: Record<string, unknown>
): string => {
  const { idempotency_key: _idempotencyKey, ...normalizedRequestBody } =
    requestBody;
  return JSON.stringify(normalizedRequestBody);
};

const readStoredCreateTicketIntent = (): CreateTicketIntent | null => {
  if (typeof window === "undefined") return inMemoryCreateTicketIntent;
  try {
    const value = window.sessionStorage.getItem(
      CREATE_TICKET_INTENT_STORAGE_KEY
    );
    if (!value) return inMemoryCreateTicketIntent;
    const parsed = JSON.parse(value) as Partial<CreateTicketIntent>;
    if (
      typeof parsed.fingerprint === "string" &&
      typeof parsed.idempotencyKey === "string"
    ) {
      return parsed as CreateTicketIntent;
    }
  } catch {
    // Fall through to the in-memory copy when storage is unavailable.
  }
  return inMemoryCreateTicketIntent;
};

const writeStoredCreateTicketIntent = (intent: CreateTicketIntent): void => {
  inMemoryCreateTicketIntent = intent;
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      CREATE_TICKET_INTENT_STORAGE_KEY,
      JSON.stringify(intent)
    );
  } catch {
    // The in-memory copy still covers retries during this page lifetime.
  }
};

const clearStoredCreateTicketIntent = (intent: CreateTicketIntent): void => {
  const current = readStoredCreateTicketIntent();
  if (
    current?.fingerprint !== intent.fingerprint ||
    current.idempotencyKey !== intent.idempotencyKey
  ) {
    return;
  }
  inMemoryCreateTicketIntent = null;
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(CREATE_TICKET_INTENT_STORAGE_KEY);
  } catch {
    // The in-memory copy has already been cleared.
  }
};

const getCreateTicketIntent = (
  requestBody: Record<string, unknown>
): CreateTicketIntent => {
  const fingerprint = createTicketIntentFingerprint(requestBody);
  const stored = readStoredCreateTicketIntent();
  if (stored?.fingerprint === fingerprint) return stored;

  const intent = { fingerprint, idempotencyKey: crypto.randomUUID() };
  writeStoredCreateTicketIntent(intent);
  return intent;
};

/** Coerce string or array to a single string for DB; join arrays with ", " (form state may have array from agent/preset). */
function joinArrayOrString(
  value: string | string[] | undefined | null
): string | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === "string") return value.trim() || undefined;
  if (Array.isArray(value)) {
    const joined = value
      .filter((s): s is string => typeof s === "string")
      .map(s => s.trim())
      .filter(Boolean)
      .join(", ");
    return joined || undefined;
  }
  return undefined;
}

/**
 * Ticket object returned from API
 */
export interface Ticket {
  id: string;
  prompt?: string;
  image_path?: string | null;
  image_url?: string | null;
  back_image_path?: string | null;
  back_prompt?: string | null;
  length?: string;
  sleeve?: string;
  sleeveAddons?: string[];
  neckline?: string;
  waistline?: string;
  silhouette?: string;
  fabric?: string;
  status?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  color?: string;
  preset_type?: string | null;
  /** Runtime-validated by consumers before it is treated as render history. */
  design_spec?: unknown;
  /** Immutable contract bound to image_url; null/absent means legacy unknown. */
  front_render_design_spec?: unknown;
  /** Immutable contract bound to back_image_url; null/absent means legacy unknown. */
  back_render_design_spec?: unknown;
  [key: string]: any; // Allow additional fields
}

function ticketFromMutationResponse(payload: any): Ticket | undefined {
  const ticket = payload?.ticket ?? payload?.data?.ticket;
  if (!ticket) return undefined;

  const quotePending = payload?.quotePending ?? payload?.data?.quotePending;
  const quoteMessage = payload?.quoteMessage ?? payload?.data?.quoteMessage;

  return {
    ...ticket,
    ...(typeof quotePending === "boolean" ? { quotePending } : {}),
    ...(typeof quoteMessage === "string" ? { quoteMessage } : {}),
  };
}

/**
 * Form data structure for ticket operations
 */
export type TicketFormData = DressDesignFields & {
  isFrontUploadPreset?: boolean;
  isBackUploadPreset?: boolean;
  uploadedFrontImages?: File[];
  uploadedBackImages?: File[];
};

/**
 * Parameters for creating a ticket
 */
export interface CreateTicketParams {
  formData: TicketFormData;
  imagePath: string | null;
  imageUrl?: string | null;
  backImagePath?: string | null;
  backPrompt?: string | null;
  status?: "generated" | "submitted";
  location?: {
    zipCode: string;
    city: string;
    state: string;
    country: string;
  };
  /** Soft reference to the create-agent session that produced this design. */
  agentSessionId?: string | null;
  /** Latest current/quote contract; per-side render provenance is server-owned. */
  designSpec?: DesignSpec;
  /** Stable across a caller-managed retry; generated automatically otherwise. */
  idempotencyKey?: string;
}

/**
 * Creates a new ticket
 */
export async function createTicket(
  params: CreateTicketParams
): Promise<Ticket> {
  const {
    formData,
    imagePath,
    imageUrl,
    backImagePath,
    backPrompt,
    status = "generated",
    location,
    agentSessionId,
    designSpec,
    idempotencyKey,
  } = params;

  if (!imagePath && !imageUrl) {
    throw new Error("Either imagePath or imageUrl must be provided");
  }

  // Prevent back image from being used as front image
  if (backImagePath && imagePath === backImagePath) {
    throw new Error("Front and back image paths cannot be the same");
  }
  if (backImagePath && imageUrl && imageUrl === backImagePath) {
    throw new Error("Front and back image paths cannot be the same");
  }

  const presetLabel = getPresetLabel(formData, "front");

  // For upload presets, use "Uploaded Image" instead of the default upload prompt
  const frontPrompt = formData.prompt.trim();
  const ticketFrontPrompt =
    frontPrompt === DEFAULT_UPLOAD_PROMPT ? UPLOAD_TICKET_PROMPT : frontPrompt;

  const requestBody: any = {
    prompt: ticketFrontPrompt,
    image_path: imagePath,
    ...(imageUrl && { image_url: imageUrl }), // Fallback if image_path is null
    length: formData.length,
    sleeve: formData.sleeve,
    sleeveAddons: formData.sleeveAddons,
    neckline: formData.neckline,
    waistline: formData.waistline,
    silhouette: formData.silhouette,
    fabric: joinArrayOrString(formData.fabric),
    status,
    color: joinArrayOrString(formData.color),
    ...(designSpec ? { design_spec: designSpec } : {}),
  };

  // Add back image fields if provided
  if (backImagePath) {
    requestBody.back_image_path = backImagePath;
  }
  if (backPrompt) {
    // For upload presets, use "Uploaded Image" instead of the default upload prompt
    const trimmedBackPrompt = backPrompt.trim();
    requestBody.back_prompt =
      trimmedBackPrompt === DEFAULT_UPLOAD_PROMPT
        ? UPLOAD_TICKET_PROMPT
        : trimmedBackPrompt;
  }

  // Add location if provided (for submission)
  if (location) {
    requestBody.city = location.city;
    requestBody.state = location.state;
    requestBody.zip_code = location.zipCode;
    requestBody.country = location.country;
  }

  // Soft link back to the create-agent session that produced this design.
  if (agentSessionId) {
    requestBody.agent_session_id = agentSessionId;
  }

  // Always include preset_type - default to "Custom" if we have a prompt but can't determine the preset
  // This ensures the field is never empty for valid tickets
  const resolvedPresetLabel =
    presetLabel || (ticketFrontPrompt ? "Custom" : null);
  if (resolvedPresetLabel) {
    requestBody.preset_type = resolvedPresetLabel;
  }

  // Keep one key for the complete logical create, not merely one fetch call.
  // Fingerprint the exact normalized payload so every request-affecting field
  // (including the resolved preset) starts a new operation when it changes.
  // If the server committed but its response was lost, an identical retry after
  // a reload still recovers the same ticket.
  let managedIntent = idempotencyKey
    ? null
    : getCreateTicketIntent(requestBody);
  requestBody.idempotency_key = idempotencyKey ?? managedIntent?.idempotencyKey;

  let response = await fetchWithAuthRetry("/api/create-ticket", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(requestBody),
  });

  if (response.status === 409 && managedIntent) {
    // The key belonged to work that could not be adopted into the current
    // account. Clear only that logical create, mint a current-identity key,
    // and retry once; successful adoption still takes the normal replay path.
    clearStoredCreateTicketIntent(managedIntent);
    managedIntent = getCreateTicketIntent(requestBody);
    requestBody.idempotency_key = managedIntent.idempotencyKey;
    response = await fetchWithAuthRetry("/api/create-ticket", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestBody),
    });
    if (response.status === 409) {
      clearStoredCreateTicketIntent(managedIntent);
    }
  }

  if (!response.ok) {
    let errorMessage = "Failed to create ticket. Please try again.";
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorData.message || errorMessage;
    } catch {
      // Use default error message
    }
    logger.error("Ticket creation failed", {
      component: "TicketService",
      metadata: {
        status: response.status,
        hasImagePath: !!imagePath,
        hasImageUrl: !!imageUrl,
      },
    });
    throw new Error(errorMessage);
  }

  const resp = await response.json();
  const ticket = ticketFromMutationResponse(resp);

  if (!ticket) {
    throw new Error("No ticket returned from API");
  }

  if (managedIntent) clearStoredCreateTicketIntent(managedIntent);
  rememberTicketCas(ticket);

  return ticket;
}

/**
 * Parameters for updating a ticket
 */
export interface UpdateTicketParams extends DressDesignUpdate {
  ticketId: string;
  status?: "generated" | "submitted";
  imagePath?: string | null;
  backImagePath?: string | null;
  location?: {
    zipCode: string;
    city: string;
    state: string;
    country: string;
  };
  /**
   * Soft reference to the create-agent session that produced this design.
   *
   * The dominant funnel path submits an EXISTING ticket (generate-image already
   * created it), so without this the update branch could never repair a NULL
   * link — which is most of why only 3 of 116 tickets could be traced back to a
   * conversation. The RPC COALESCEs, so a null here preserves what is stored.
   */
  agentSessionId?: string | null;
  /** Latest current/quote contract; per-side render provenance is server-owned. */
  designSpec?: DesignSpec;
}

export const TICKET_CANONICAL_FIELDS_LOCKED_CODE =
  "TICKET_CANONICAL_FIELDS_LOCKED";

export class TicketCanonicalFieldsLockedError extends Error {
  readonly status = 409;
  readonly code = TICKET_CANONICAL_FIELDS_LOCKED_CODE;

  constructor(message = "Ticket design is locked after submission") {
    super(message);
    this.name = "TicketCanonicalFieldsLockedError";
  }
}

export function ticketAllowsCanonicalDesignUpdates(
  status: string | null | undefined
): boolean {
  return (
    status === "generated" || status === "cancelled" || status === "rejected"
  );
}

/**
 * Updates an existing ticket
 */
export async function updateTicket(
  params: UpdateTicketParams
): Promise<Ticket | undefined> {
  const {
    ticketId,
    status,
    imagePath,
    backImagePath,
    prompt,
    backPrompt,
    location,
    color,
    preset_type,
    length,
    sleeve,
    sleeveAddons,
    neckline,
    waistline,
    silhouette,
    fabric,
    agentSessionId,
    designSpec,
  } = params;

  const requestBody: any = {
    ticket_id: ticketId,
  };

  if (status === "generated") {
    const expectedCas = readTicketCas(ticketId);
    if (expectedCas) {
      requestBody.expected_updated_at = expectedCas.updatedAt;
      requestBody.expected_revision_counter = expectedCas.revisionCounter;
    }
  }

  if (status) {
    requestBody.status = status;
  }
  // Sent whenever the client knows it, including on plain updates: the server
  // COALESCEs, so this repairs a ticket that was created before the session id
  // existed and never overwrites a stored link with null.
  if (agentSessionId) {
    requestBody.agent_session_id = agentSessionId;
  }
  if (designSpec) {
    requestBody.design_spec = designSpec;
  }
  if (imagePath !== undefined) {
    requestBody.image_path = imagePath;
  }
  if (backImagePath !== undefined) {
    requestBody.back_image_path = backImagePath;
  }
  if (prompt !== undefined && prompt !== null) {
    requestBody.prompt = prompt;
  }
  if (backPrompt !== undefined && backPrompt !== null) {
    requestBody.back_prompt = backPrompt;
  }
  if (location) {
    requestBody.city = location.city;
    requestBody.state = location.state;
    requestBody.zip_code = location.zipCode;
    requestBody.country = location.country;
  }
  const colorStr = joinArrayOrString(color);
  if (colorStr !== undefined) {
    requestBody.color = colorStr;
  }
  if (preset_type !== undefined && preset_type !== null) {
    requestBody.preset_type = preset_type;
  }
  if (length !== undefined && length !== null) {
    requestBody.length = length;
  }
  if (sleeve !== undefined && sleeve !== null) {
    requestBody.sleeve = sleeve;
  }
  if (sleeveAddons !== undefined && sleeveAddons !== null) {
    requestBody.sleeveAddons = sleeveAddons;
  }
  if (neckline !== undefined && neckline !== null) {
    requestBody.neckline = neckline;
  }
  if (waistline !== undefined && waistline !== null) {
    requestBody.waistline = waistline;
  }
  if (silhouette !== undefined && silhouette !== null) {
    requestBody.silhouette = silhouette;
  }
  const fabricStr = joinArrayOrString(fabric);
  if (fabricStr !== undefined) {
    requestBody.fabric = fabricStr;
  }

  const response = await fetchWithAuthRetry("/api/update-ticket", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    let errorMessage = "Failed to update ticket. Please try again.";
    let errorCode: string | undefined;
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorData.message || errorMessage;
      errorCode = errorData.details?.code;
    } catch {
      // Use default error message
    }
    const isCanonicalLockConflict =
      response.status === 409 &&
      errorCode === TICKET_CANONICAL_FIELDS_LOCKED_CODE;
    logger[isCanonicalLockConflict ? "warn" : "error"]("Ticket update failed", {
      component: "TicketService",
      metadata: {
        status: response.status,
        ticketId,
        ...(errorCode ? { code: errorCode } : {}),
      },
    });
    if (isCanonicalLockConflict) {
      throw new TicketCanonicalFieldsLockedError(errorMessage);
    }
    throw new Error(errorMessage);
  }

  const payload = await response.json();
  const ticket = ticketFromMutationResponse(payload);
  if (ticket) rememberTicketCas(ticket);
  return ticket;
}

/**
 * Fetches ticket data for prefill
 */
export const TICKET_RESTORE_TIMEOUT_MESSAGE =
  "This dress took too long to load. Try again.";

export class TicketRestoreTimeoutError extends Error {
  constructor() {
    super(TICKET_RESTORE_TIMEOUT_MESSAGE);
    this.name = "TicketRestoreTimeoutError";
  }
}

export async function getTicket(
  ticketId: string,
  options: { signal?: AbortSignal; timeoutMs?: number } = {}
): Promise<Ticket> {
  const controller = new AbortController();
  let timedOut = false;
  const abortFromCaller = () => controller.abort(options.signal?.reason);
  if (options.signal?.aborted) abortFromCaller();
  else
    options.signal?.addEventListener("abort", abortFromCaller, { once: true });

  // The auth helper may perform one short 401 retry, so give local auth and
  // the API ample time while still guaranteeing that restoration terminates.
  const timeout = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, options.timeoutMs ?? 20_000);

  try {
    const response = await fetchWithAuthRetry(
      "/api/ticket-data",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ticketId }),
        signal: controller.signal,
      },
      1, // retry once on 401
      800
    );

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error("Ticket not found");
      } else if (response.status === 403) {
        throw new Error("Access denied");
      } else if (response.status === 401) {
        throw new Error("Authentication required");
      } else {
        throw new Error(`Failed to fetch ticket: ${response.statusText}`);
      }
    }

    // Keep the same deadline and caller cancellation alive until the body is
    // decoded. Fetch can resolve as soon as headers arrive; clearing the timer
    // at that point leaves restoration hanging forever when the body stalls.
    const data = await response.json();
    const ticket = data.data?.ticket;

    if (!ticket) {
      throw new Error("No ticket data returned from API");
    }

    rememberTicketCas(ticket);
    return ticket;
  } catch (error) {
    if (timedOut) throw new TicketRestoreTimeoutError();
    throw error;
  } finally {
    clearTimeout(timeout);
    options.signal?.removeEventListener("abort", abortFromCaller);
  }
}

/**
 * Parameters for submitting a ticket
 */
export interface SubmitTicketParams {
  ticketId: string | null;
  formData: TicketFormData;
  imagePath: string;
  backImagePath?: string | null;
  /** An explicit front-only quote must not reuse a row with saved Back fields. */
  quoteScope?: "front_only";
  location: {
    zipCode: string;
    city: string;
    state: string;
    country: string;
  };
  isModificationMode?: boolean;
  /** Soft reference to the create-agent session that produced this design. */
  agentSessionId?: string | null;
  /** Current canonical quote contract, which may differ from rendered images. */
  designSpec?: DesignSpec;
  /** Retry only the quote sidecar for a ticket already committed by this flow. */
  retryCommittedSubmission?: boolean;
}

function ticketAlreadyHasInstantQuote(ticket: Ticket): boolean {
  return (
    ticket.status === "approved" ||
    (typeof ticket.price === "number" && ticket.price > 0) ||
    Boolean(ticket.selected_quote_id)
  );
}

async function requestInstantQuoteForTicket(ticketId: string): Promise<Ticket> {
  const response = await fetchWithAuthRetry(
    `/api/ticket/${encodeURIComponent(ticketId)}/instant-quote`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    }
  );

  if (!response.ok) {
    let errorMessage = "Failed to generate instant quote. Please try again.";
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorData.message || errorMessage;
    } catch {
      // The create-page action maps this internal detail to customer-safe copy.
    }
    throw new Error(errorMessage);
  }

  return getTicket(ticketId);
}

/**
 * A restored design can already have crossed the quote boundary in another
 * tab or a previous request. If replaying `submitted` loses that race, recover
 * from the exact owned ticket instead of surfacing the database transition.
 */
async function recoverExistingQuoteAfterSubmissionFailure(
  ticketId: string
): Promise<Ticket | null> {
  try {
    const currentTicket = await getTicket(ticketId);
    if (ticketAlreadyHasInstantQuote(currentTicket)) {
      return currentTicket;
    }
    if (currentTicket.status === "submitted") {
      return requestInstantQuoteForTicket(ticketId);
    }
    if (!ticketAllowsCanonicalDesignUpdates(currentTicket.status)) {
      return currentTicket;
    }
  } catch {
    // Preserve the original submission failure. The action layer owns the one
    // customer-safe retry surface and must never expose either server error.
  }
  return null;
}

/**
 * Submits a ticket for production
 * Creates a new ticket if ticketId is null, otherwise updates existing ticket
 */
export async function submitTicket(
  params: SubmitTicketParams
): Promise<Ticket> {
  const {
    ticketId,
    formData,
    imagePath,
    backImagePath,
    quoteScope,
    location,
    isModificationMode = false,
    agentSessionId,
    designSpec,
    retryCommittedSubmission = false,
  } = params;

  if (retryCommittedSubmission && ticketId) {
    // The first mutation may already have moved this ticket from submitted to
    // approved before the client readiness poll timed out. Replaying the
    // status transition would be rejected (approved -> submitted) and can also
    // rewrite submission side effects. Retry only the idempotent quote sidecar.
    return requestInstantQuoteForTicket(ticketId);
  }

  if (isModificationMode || !ticketId || quoteScope === "front_only") {
    // Create new ticket for submission
    return createTicket({
      formData,
      imagePath,
      backImagePath: quoteScope === "front_only" ? null : backImagePath || null,
      backPrompt:
        quoteScope === "front_only"
          ? undefined
          : formData.backPrompt || undefined,
      status: "submitted",
      location,
      agentSessionId,
      designSpec,
    });
  } else {
    // Restored sessions, auth/payment handoffs, and another tab can leave the
    // browser holding a ticket that already crossed the submission boundary.
    // Read its authoritative state before constructing a canonical PUT. A
    // submitted ticket needs only the idempotent quote sidecar; an approved or
    // already-priced ticket is complete. This prevents stale form state from
    // replaying immutable fields after submission.
    const currentTicket = await getTicket(ticketId);
    if (ticketAlreadyHasInstantQuote(currentTicket)) {
      return currentTicket;
    }
    if (currentTicket.status === "submitted") {
      return requestInstantQuoteForTicket(ticketId);
    }
    if (!ticketAllowsCanonicalDesignUpdates(currentTicket.status)) {
      return currentTicket;
    }

    // Only generated, cancelled, and rejected rows may receive the canonical
    // submission snapshot. Checkout/payment handoffs can restore any later
    // order state; those rows are already beyond the mutable design boundary.
    const presetLabel = getPresetLabel(formData, "front");
    let updatedTicket: Ticket | undefined;
    try {
      updatedTicket = await updateTicket({
        ticketId,
        status: "submitted",
        location,
        agentSessionId,
        designSpec,
        prompt: formData.prompt || undefined,
        color: joinArrayOrString(formData.color),
        preset_type: presetLabel || undefined,
        length: formData.length || undefined,
        sleeve: formData.sleeve || undefined,
        sleeveAddons: formData.sleeveAddons,
        neckline: formData.neckline || undefined,
        waistline: formData.waistline || undefined,
        silhouette: formData.silhouette || undefined,
        fabric: joinArrayOrString(formData.fabric),
        backPrompt: formData.backPrompt || undefined,
      });
    } catch (error) {
      const recoveredTicket =
        await recoverExistingQuoteAfterSubmissionFailure(ticketId);
      if (recoveredTicket) return recoveredTicket;
      throw error;
    }

    // The update itself is committed even if the quote sidecar failed. Carry
    // that ticket and state back immediately so the create page can retry the
    // quote on the same id rather than waiting and replaying submission.
    if (updatedTicket?.quotePending) {
      return updatedTicket;
    }

    // Fetch the updated ticket to return
    return getTicket(ticketId);
  }
}

/**
 * Parameters for creating a modified ticket
 */
export interface CreateModifiedTicketParams {
  formData: TicketFormData;
  frontImageUrl: string;
  frontImagePath: string | null;
  modificationPrompt: string;
  backImageUrl?: string | null;
  backImagePath?: string | null;
  isModifyingFront?: boolean;
  currentTicketId?: string | null;
  /**
   * Soft reference to the create-agent session. Every modification creates a
   * NEW ticket that immediately becomes THE ticket, so omitting it here orphans
   * the design from its conversation for the rest of the funnel.
   */
  agentSessionId?: string | null;
  /** Canonical contract produced by the successful modification render. */
  designSpec?: DesignSpec;
}

/**
 * Creates a ticket for modifications
 * Uses formData prompts (which already contain original prompts) as the base for aggregation.
 * Appends modifications in the format: ORIGINAL_PROMPT; MODIFICATION_1; MODIFICATION_2; ...
 */
export async function createModifiedTicket(
  params: CreateModifiedTicketParams
): Promise<Ticket> {
  const {
    formData,
    frontImageUrl,
    frontImagePath,
    modificationPrompt,
    backImagePath,
    isModifyingFront = false,
    currentTicketId,
    agentSessionId,
    designSpec,
  } = params;

  const trimmedModPrompt = modificationPrompt.trim();

  // Use formData prompts as the base (they already contain original prompts)
  // When currentTicketId is null (modification mode), use only formData prompts
  // When currentTicketId is provided (non-modification mode), prefer formData but check ticket as fallback
  // Convert DEFAULT_UPLOAD_PROMPT to UPLOAD_TICKET_PROMPT for consistency
  let baseFrontPrompt = formData.prompt.trim();
  if (baseFrontPrompt === DEFAULT_UPLOAD_PROMPT) {
    baseFrontPrompt = UPLOAD_TICKET_PROMPT;
  }
  let baseBackPrompt = formData.backPrompt?.trim() || "";
  if (baseBackPrompt === DEFAULT_UPLOAD_PROMPT) {
    baseBackPrompt = UPLOAD_TICKET_PROMPT;
  }

  // Only fetch from ticket if currentTicketId is provided AND formData prompt is empty
  // This handles legacy cases where formData might not have the prompt
  if (currentTicketId && !baseFrontPrompt) {
    try {
      const currentTicket = await getTicket(currentTicketId);
      if (currentTicket.prompt) {
        baseFrontPrompt = currentTicket.prompt.trim();
        // Convert DEFAULT_UPLOAD_PROMPT to UPLOAD_TICKET_PROMPT for consistency
        if (baseFrontPrompt === DEFAULT_UPLOAD_PROMPT) {
          baseFrontPrompt = UPLOAD_TICKET_PROMPT;
        }
      }
      if (currentTicket.back_prompt && !baseBackPrompt) {
        baseBackPrompt = currentTicket.back_prompt.trim();
        // Convert DEFAULT_UPLOAD_PROMPT to UPLOAD_TICKET_PROMPT for consistency
        if (baseBackPrompt === DEFAULT_UPLOAD_PROMPT) {
          baseBackPrompt = UPLOAD_TICKET_PROMPT;
        }
      }
    } catch (error) {
      // If ticket fetch fails, log and continue with formData prompts
      logger.warn(
        "Failed to fetch current ticket for prompt aggregation",
        {
          component: "ticketService",
          metadata: { ticketId: currentTicketId },
        },
        error as Error
      );
    }
  }

  // Helper function to check if the modification prompt already contains the base prompt
  // This prevents duplication: if base is "A; B" and mod is "A; B; C", don't append again
  const shouldAppendModification = (
    basePrompt: string,
    modification: string
  ): boolean => {
    if (!basePrompt) return true;
    // Normalize by trimming
    const normalizedBase = basePrompt.trim();
    const normalizedMod = modification.trim();

    // Check if modification already starts with the base prompt (exact prefix match)
    // This handles cases like: base="A; B", mod="A; B; C" -> don't append
    if (normalizedMod.startsWith(normalizedBase)) {
      // Check if it's followed by "; " or is exactly the same
      const remaining = normalizedMod.slice(normalizedBase.length).trim();
      if (remaining === "" || remaining.startsWith(";")) {
        return false; // Base is already included, don't append
      }
    }

    // Check if modification contains the base as a complete semicolon-separated segment
    // Split both by semicolon and check if all base segments are in mod
    const baseSegments = normalizedBase
      .split(";")
      .map(s => s.trim())
      .filter(Boolean);
    const modSegments = normalizedMod
      .split(";")
      .map(s => s.trim())
      .filter(Boolean);

    // If all base segments are present in mod in the same order, don't append
    if (baseSegments.length > 0) {
      let baseIndex = 0;
      for (const modSegment of modSegments) {
        if (
          baseIndex < baseSegments.length &&
          modSegment === baseSegments[baseIndex]
        ) {
          baseIndex++;
        }
      }
      // If we matched all base segments, the base is already included
      if (baseIndex === baseSegments.length) {
        return false;
      }
    }

    // Otherwise, append the modification
    return true;
  };

  // Build front prompt - append modification if modifying front
  // Format: ORIGINAL_PROMPT; MODIFICATION_1; MODIFICATION_2; ...
  const frontPrompt = isModifyingFront
    ? baseFrontPrompt &&
      shouldAppendModification(baseFrontPrompt, trimmedModPrompt)
      ? `${baseFrontPrompt}; ${trimmedModPrompt}`
      : baseFrontPrompt || trimmedModPrompt
    : baseFrontPrompt;

  // Build back prompt - append modification if modifying back
  // When modifying front, keep the existing back prompt unchanged
  // Format: ORIGINAL_BACK_PROMPT; MODIFICATION_1; MODIFICATION_2; ...
  const backPrompt = isModifyingFront
    ? baseBackPrompt || null // Keep existing back prompt when modifying front
    : baseBackPrompt &&
        shouldAppendModification(baseBackPrompt, trimmedModPrompt)
      ? `${baseBackPrompt}; ${trimmedModPrompt}`
      : baseBackPrompt || trimmedModPrompt; // Use only modification prompt when no existing back prompt

  return createTicket({
    formData: {
      ...formData,
      prompt: frontPrompt,
      backPrompt: backPrompt || undefined,
    },
    imagePath: frontImagePath,
    imageUrl: frontImageUrl,
    backImagePath: backImagePath || null,
    backPrompt,
    status: "generated",
    agentSessionId,
    designSpec,
  });
}
